/**
 * Axios Interceptor for Offline Support
 * Intercepts requests and queues them when offline
 */

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { v4 as uuidv4 } from 'uuid';
import offlineManager from './offlineManager';
import db from './db';
import store from '../Redux/store';

/**
 * Setup axios interceptors for offline support
 */
export const setupOfflineInterceptor = () => {
  
  // Auth/security endpoints and report endpoints that should never be retried or queued
  const authRetryExclude = /(checkdevice|requestdeviceotp|verifydeviceotp|login|trusteddevices|removedevice|searchuser|accountReceivableAgingReport|accountPayableAgingReport|singleCustomerARAR|accountReceivableReport|accountPayableReport|agingReport|Report$)/i;

  // Configure axios-retry for automatic retries
  axiosRetry(axios, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      const url = error.config?.url || '';
      // Skip all retry logic for non-backend requests (e.g. localhost:5000 print utility)
      const isExternalRequest = url.startsWith('http://') || url.startsWith('https://');
      if (isExternalRequest) return false;
      // Never retry auth endpoints - they must fail fast
      if (authRetryExclude.test(url)) return false;
      // Never retry when backend is known to be down - let offline fallback handle it
      if (window._backendHealthy === false) return false;
      // Never retry sync engine requests - they have their own retry logic
      if (error.config?._isSyncRetry) return false;
      // If this is a network error (no response = connection refused / server down),
      // mark backend as unhealthy immediately so THIS and all concurrent requests stop retrying
      if (!error.response && error.code !== 'ECONNABORTED') {
        console.log(`📴 Network error on ${url} - marking backend unhealthy, skipping retries`);
        window._backendHealthy = false;
        return false;
      }
      // Only retry on 5xx server errors (backend responded but with server error)
      return error.response && error.response.status >= 500;
    },
    onRetry: (retryCount, error, requestConfig) => {
      console.log(`Retry attempt ${retryCount} for ${requestConfig.url}`);
    }
  });

  /**
   * Request Interceptor
   * Check if online before making request
   */
  axios.interceptors.request.use(
    async (config) => {
      const skipCache = String(config?.headers?.['x-skip-cache'] || config?.headers?.['X-Skip-Cache'] || '').toLowerCase() === 'true';

      // Skip interceptor for certain endpoints (like health checks)
      if (config.url?.includes('/health') || config.skipOfflineCheck) {
        return config;
      }

      // Skip interceptor for non-backend requests (e.g. localhost:5000 print utility)
      const reqUrl = config.url || '';
      if (reqUrl.startsWith('http://') || reqUrl.startsWith('https://')) {
        return config;
      }

      // Support forced offline handling (e.g., server down while network up)
      const forceOffline = Boolean(config._forceOffline);

      // ONLY queue if ACTUALLY offline (navigator.onLine is false) or forced via fallback
      const actuallyOffline = forceOffline || !navigator.onLine;
      const method = config.method?.toLowerCase();
      
      // Endpoints that should NEVER be served from cache (write operations)
      const noCacheEndpoints = [
        'cancelOngoingOrders',
        'addPosOrder',
        'postOrderFromFloorPlan',
        'updatePosOrder',
        'deletePosOrder',
        'addPosOrderHold',
        'postPosPayment',
        'refundAndReturnOrd',
        'postPaySlip', // ADDED - Payslip posting should never be cached
        'empCheckOut', // Employee checkout - queue for sync
        'viewExpenseReport', // Reports must always be fresh
        'cancel',
        'delete',
        'update',
        'add',
        'create',
        'remove'
      ];
      
      const isWriteOperation = noCacheEndpoints.some(endpoint => 
        config.url?.toLowerCase().includes(endpoint.toLowerCase())
      );
      
      // If offline, handle the request
      if (actuallyOffline && (method === 'get' || method === 'post')) {
        // Normalize cache key to match response interceptor caching (strip domain)
        const cacheKey = config.url?.replace(/^https?:\/\/[^\/]+/, '') || config.url;
        
        console.log(`📴 Offline${forceOffline ? ' (forced)' : ''}: Attempting to handle ${config.url}`);
        
        try {
          // SPECIAL CASE: empCheckOutPaymentReport - Employee checkout payment report
          // This is a GET request that verifies password and fetches payment summary
          // For offline, we calculate from locally cached orders
          if (config.url?.includes('empCheckOutPaymentReport')) {
            console.log(`📴 Offline: Employee checkout payment report requested`);
            
            // Extract shiftId from query params
            const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
            const shiftId = urlParams.get('shiftId') || config.params?.shiftId;
            
            // Calculate checkout report from local orders
            let reportData;
            if (shiftId) {
              reportData = await db.calculateOfflineCheckoutReport(shiftId);
            } else {
              reportData = {
                success: true,
                offline: true,
                message: 'No shiftId provided for offline checkout report',
                data: {
                  cashCardSummary: { data: [], amountTotal: 0 },
                  posOrderSummary: []
                }
              };
            }
            
            // Return the calculated report
            const mockResponse = {
              data: reportData,
              status: 200,
              statusText: 'OK',
              headers: { 'x-offline': 'true' },
              config,
              request: {}
            };
            
            const error = new Error('Checkout report served offline');
            error.isOfflineQueue = true;
            error.mockResponse = mockResponse;
            throw error;
          }
          
          // SPECIAL CASE: empShiftLogOutPaymentReport - Shift end payment report
          // Similar to checkout report, calculate from locally cached orders
          if (config.url?.includes('empShiftLogOutPaymentReport')) {
            console.log(`📴 Offline: Shift end payment report requested`);
            
            // Extract shiftId from query params
            const urlParams = new URLSearchParams(config.url?.split('?')[1] || '');
            const shiftId = urlParams.get('shiftId') || config.params?.shiftId;
            
            // Calculate report from local orders (same logic as checkout)
            let reportData;
            if (shiftId) {
              reportData = await db.calculateOfflineCheckoutReport(shiftId);
              reportData.message = 'Shift end report calculated from local data';
            } else {
              reportData = {
                success: true,
                offline: true,
                message: 'No shiftId provided for offline shift end report',
                data: {
                  cashCardSummary: { data: [], amountTotal: 0 },
                  posOrderSummary: []
                }
              };
            }
            
            // Return the calculated report
            const mockResponse = {
              data: reportData,
              status: 200,
              statusText: 'OK',
              headers: { 'x-offline': 'true' },
              config,
              request: {}
            };
            
            const error = new Error('Shift end report served offline');
            error.isOfflineQueue = true;
            error.mockResponse = mockResponse;
            throw error;
          }
          
          // SPECIAL CASE: viewPosOrderForInvoice is POST but reads offline orders
          // Check this BEFORE the write operation check
          if (config.url?.includes('viewPosOrderForInvoice')) {
            const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
            const orderId = requestData.id || requestData.orderId || requestData._id;
            console.log(`📄 Checking if receipt order is in cache: ${orderId}`);
            
            try {
              // Try to get offline order first
              let order = await db.getOfflineOrderById(orderId);
              
              // If not found in offline orders, check all cached orders
              if (!order) {
                console.log(`   Order not in offline orders, checking cache...`);
                order = await db.ordersCache.get(orderId);
              }
              
              if (order) {
                console.log(`✅ Returning ${order.offlineOrder ? 'offline' : 'cached'} order receipt: ${orderId}`);
                
                // Return order from IndexedDB (not queued!)
                const mockResponse = {
                  data: order,  // Return the order directly
                  status: 200,
                  statusText: 'OK',
                  headers: { 'x-offline': 'true' },
                  config,
                  request: {}
                };
                
                const error = new Error('Order receipt served from IndexedDB');
                error.isOfflineQueue = true;
                error.mockResponse = mockResponse;
                throw error;
              }
              // If not found anywhere, continue to normal flow (will be queued below)
              console.log(`📄 Order ${orderId} not found in cache, will queue`);
            } catch (error) {
              // If it's our custom error, re-throw it
              if (error.isOfflineQueue) {
                throw error;
              }
              console.error('❌ Error checking order for receipt:', error);
            }
          }
          
          // Write operations should ALWAYS be queued, never served from cache
          // BUT: Skip if this is a sync retry (already queued) to prevent duplicates
          if (isWriteOperation && method === 'post') {
            // Check if this is a sync engine retry - don't re-queue!
            if (config._isSyncRetry) {
              console.log(`📴 Offline: Sync retry detected, NOT re-queuing ${config.url}`);
              const error = new Error('Sync failed - server unreachable');
              error.isSyncFailure = true;
              throw error;
            }
            
            // Endpoints that have their own specific queueTransaction call below
            // should NOT be queued here to prevent double-queuing
            const hasCustomHandler = [
              'postPosPayment',
              'cancelOngoingOrders',
              'deleteBookingOrder',
              'empCheckOut',
              'addItemsToExistingOrders'
            ].some(ep => config.url?.includes(ep));
            
            let result = { transactionId: null };
            if (!hasCustomHandler) {
              console.log(`📴 Offline: Write operation detected, queuing ${config.url}`);
              result = await offlineManager.queueTransaction(config);
            } else {
              console.log(`📴 Offline: Write operation with custom handler, deferring queue for ${config.url}`);
            }
            
            // Create a realistic mock response that mimics online behavior
            const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
            
            // Generate mock order data for addPosOrder
            let mockData = {
              success: true,
              queued: true,
              transactionId: result.transactionId,
              message: 'Order created offline - will sync when online'
            };
            
            // For addPosOrder or postOrderFromFloorPlan, create a full mock order response
            if (config.url?.includes('addPosOrder') || config.url?.includes('postOrderFromFloorPlan')) {
              // Generate sequential order number
              let orderNumber = 1;
              try {
                // Get cached online orders using the same method as listing
                const cachedOrders = await offlineManager.getCachedResponse('pos/listPosOrders') || [];
                
                // Get offline orders
                const offlineOrders = await db.getOfflineOrders();
                
                // Merge both to get ALL orders
                const allOrders = [...offlineOrders, ...cachedOrders];
                
                console.log(`📊 Found ${cachedOrders.length} cached orders + ${offlineOrders.length} offline orders = ${allOrders.length} total`);
                
                if (allOrders.length > 0) {
                  // Get the highest transNo from ALL orders (online + offline)
                  const allTransNos = allOrders
                    .map(o => o.transNo || 0)
                    .filter(n => n > 0);
                  
                  if (allTransNos.length > 0) {
                    const maxTransNo = Math.max(...allTransNos);
                    orderNumber = maxTransNo + 1;
                    console.log(`📊 Generated TEMPORARY offline order number: ${orderNumber} (max was ${maxTransNo} from ${allOrders.length} orders)`);
                    console.log(`⚠️ NOTE: Server will assign the REAL order number during sync`);
                  } else {
                    // No valid transNo found, use localStorage or default
                    const storedCounter = localStorage.getItem('offlineOrderCounter');
                    orderNumber = storedCounter ? parseInt(storedCounter) + 1 : 1;
                    console.log(`📊 Generated TEMPORARY offline order number from localStorage: ${orderNumber}`);
                  }
                } else {
                  // No orders at all, start fresh
                  orderNumber = 1;
                  console.log(`📊 No existing orders, starting TEMPORARY order from: ${orderNumber}`);
                }
                
                // Store for next offline order
                localStorage.setItem('offlineOrderCounter', orderNumber.toString());
              } catch (error) {
                console.error('❌ Error generating order number:', error);
                // Fallback to localStorage or timestamp
                const storedCounter = localStorage.getItem('offlineOrderCounter');
                orderNumber = storedCounter ? parseInt(storedCounter) + 1 : Math.floor(Date.now() / 1000) % 10000;
                console.log(`⚠️ Using fallback order number: ${orderNumber}`);
              }
              
              // Map orderInfo to add productName for receipt display
              const mappedOrderInfo = (requestData.orderInfo || []).map(item => ({
                ...item,
                productName: item.desccription || item.productName || '',  // Map desccription to productName
                desccription: item.desccription || item.productName || ''  // Keep both fields
              }));
              
              // Get current date and time for order
              const now = new Date();
              const orderDateOnly = now.toISOString().split('T')[0]; // YYYY-MM-DD
              const orderTimeOnly = now.toTimeString().split(' ')[0]; // HH:MM:SS
              const orderDateTime = `${orderDateOnly} ${orderTimeOnly}`; // Combined: "YYYY-MM-DD HH:MM:SS"
              
              const offlineOrder = {
                // Use transactionId as temporary order ID
                _id: result.transactionId,
                orderId: result.transactionId,  // For compatibility
                // Spread ALL original request data FIRST
                ...requestData,
                // Override/ensure these fields match online order structure EXACTLY
                ordStatus: 'PENDING',
                offlineOrder: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                orderDate: orderDateTime,  // Combined: "YYYY-MM-DD HH:MM:SS" - for getTimeDifference in payment
                orderTime: orderTimeOnly,  // Time in HH:MM:SS format - used by HeldOrderGridView for timer
                transNo: orderNumber,  // TEMPORARY transaction number (server will assign real one)
                ordName: `BIL/${orderNumber}`,  // Order name - clean for customer receipts
                name: `BIL/${orderNumber}`,  // Display name - clean for customer receipts
                
                // Arrays - ensure they exist
                tableNames: requestData.tableNames || [],
                chairNames: requestData.chairNames || [],
                orderInfo: mappedOrderInfo,  // Use mapped orderInfo with productName
                
                // Numeric fields - ensure numbers not NaN
                subTotal: Number(requestData.subTotal) || 0,
                discountAmt: Number(requestData.discountAmt) || 0,
                amtTotal: Number(requestData.amtTotal) || 0,
                totalInclusive: Number(requestData.totalInclusive) || 0,
                totalExclusive: Number(requestData.totalExclusive) || 0,
                taxAmt: Number(requestData.taxAmt) || 0,
                serviceCharge: Number(requestData.serviceCharge) || 0,
                deliveryCharge: Number(requestData.deliveryCharge) || 0,
                selfDeliveryKm: Number(requestData.selfDeliveryKm) || 0,  // KM for delivery orders
                packagingCharge: Number(requestData.packagingCharge || requestData.pkgCharge) || 0,
                pkgCharge: Number(requestData.pkgCharge || requestData.packagingCharge) || 0,  // Alias for packagingCharge
                roundOffAmt: Number(requestData.roundOffAmt) || 0,
                roundOff: Number(requestData.roundOff || requestData.roundOffAmt) || 0,  // Alias for roundOffAmt
                
                // String fields
                ordType: requestData.ordType || 'DINE_IN',
                ordStatus: requestData.ordStatus || 'PENDING',  // Order status: PENDING, COMPLETED, CANCELLED
                paymentStatus: requestData.paymentStatus || 'PENDING',
                customerName: requestData.customerName || 'cash customer',  // Default to 'cash customer' like online orders
                customerMobile: requestData.customerMobile || '',
                floorName: requestData.floorName || '',
                aggregatorName: requestData.aggregatorName || '',
                deliveryAddress: requestData.deliveryAddress || '',
                custAddress: requestData.custAddress || {},  // Customer address object for display
                deliveryBoyName: requestData.deliveryBoyName || null,  // Delivery boy name
                notes: requestData.notes || '',
                
                // Boolean fields
                isEdited: false,
                isPaid: false,
                isCancelled: false,
                
                // Additional fields for receipt template
                paymentDetails: requestData.paymentDetails || [],
                taxDetails: requestData.taxDetails || [],
                waiterName: requestData.waiterName || '-NIL-',
                onGoing: requestData.onGoing !== undefined ? requestData.onGoing : 1,  // 1 = ongoing, 0 = completed
                
                // IDs
                branchId: requestData.branchId || '',
                shiftId: requestData.shiftId || '',
                empId: requestData.empId || '',
                cusId: requestData.cusId || '',
                userId: requestData.userId || '',
                customerId: requestData.customerId || '',
                floorId: requestData.floorId || '',
                tableId: requestData.tableId || '',
                aggregatorId: requestData.aggregatorId || null,  // Delivery aggregator ID
                deliveryBoyId: requestData.deliveryBoyId || null,  // Delivery boy ID
                
                // Add branch/company profile data for offline receipts
                ...((() => {
                  const state = store.getState();
                  const companyProfile = state?.companyProfileSlice?.userValue;
                  const branches = state?.allBranchesSlice?.value || [];
                  const currentBranch = branches.find(b => b._id === requestData.branchId);
                  
                  return {
                    nameOfStore: currentBranch?.name || companyProfile?.cmpName || '',
                    email: currentBranch?.email || companyProfile?.email || '',
                    contactNumber: currentBranch?.contactNumber || companyProfile?.contactNumber || '',
                    trnNumber: currentBranch?.trnNumber || companyProfile?.trnNumber || '',
                    logo: companyProfile?.logo || '',
                    db: companyProfile?.db || localStorage.getItem('db') || ''
                  };
                })())
              };
              
              // Store offline order in IndexedDB BEFORE returning response
              try {
                await db.storeOfflineOrder(offlineOrder);
                console.log(`✅ Offline order stored in IndexedDB: ${offlineOrder._id}`);
                console.log(`   Order has ${offlineOrder.orderInfo?.length || 0} items`);
                console.log(`   Order status: ${offlineOrder.ordStatus}, onGoing: ${offlineOrder.onGoing}`);
              } catch (error) {
                console.error('❌ Error storing offline order:', error);
              }
              
              // Generate KOT token number (separate from bill number)
              // KOT tokens are sequential per shift, starting from 1
              let kotToken = 1;
              try {
                kotToken = await db.getNextKotToken(
                  requestData.branchId,
                  requestData.shiftId
                );
                console.log(`🎫 Generated KOT token: ${kotToken} for order ${offlineOrder.ordName}`);
              } catch (error) {
                console.error('❌ Error generating KOT token:', error);
                kotToken = 1; // Fallback
              }
              
              // Generate KOT data for offline orders (needed for printing)
              // Format date as DD/MM/YYYY HH:MM:SS for KOT display
              const formattedDate = (() => {
                const d = new Date();
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const seconds = String(d.getSeconds()).padStart(2, '0');
                return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
              })();
              
              const kotData = {
                name: `${kotToken}`,  // KOT name is just the token number
                ordName: offlineOrder.ordName,  // Bill number (BIL/37838)
                orderDate: formattedDate,  // Formatted as DD/MM/YYYY HH:MM:SS
                ordType: offlineOrder.ordType,
                waiterName: requestData.waiterName || '-NIL-',
                orderInfo: mappedOrderInfo,  // Use mapped orderInfo with productName
                tables: requestData.tableNames || [],
                floorName: requestData.floorName || '',
                transNo: kotToken  // KOT token number (1, 2, 3, etc.)
              };
              
              // Add kotInfo to the order for receipt display
              offlineOrder.kotInfo = [`${kotToken}`];  // Array of KOT token numbers
              
              // Update the stored order with kotInfo
              try {
                await db.cacheOrder(offlineOrder);
                console.log(`✅ Updated offline order with KOT token: ${kotToken}`);
              } catch (error) {
                console.error('❌ Failed to update order with KOT token:', error);
              }
              
              mockData = {
                data: offlineOrder,
                kotData: kotData, // Generate KOT data for offline printing
                onGoing: offlineOrder.onGoing,
                success: true,
                message: 'Order created offline'
              };
            }
            
            // For addQuickBill, create order with payment in one step
            if (config.url?.includes('addQuickBill')) {
              // Generate sequential order number
              let orderNumber = 1;
              try {
                const cachedOrders = await offlineManager.getCachedResponse('pos/listPosOrders') || [];
                const offlineOrders = await db.getOfflineOrders();
                const allOrders = [...offlineOrders, ...cachedOrders];
                
                console.log(`📊 Found ${cachedOrders.length} cached orders + ${offlineOrders.length} offline orders = ${allOrders.length} total`);
                
                if (allOrders.length > 0) {
                  const maxTransNo = Math.max(...allOrders.map(o => o.transNo || 0));
                  orderNumber = maxTransNo + 1;
                }
                
                console.log(`📊 Generated TEMPORARY offline order number: ${orderNumber} (max was ${orderNumber - 1} from ${allOrders.length} orders)`);
                console.log(`⚠️ NOTE: Server will assign the REAL order number during sync`);
              } catch (error) {
                console.error('Error calculating order number:', error);
              }
              
              const now = new Date();
              const orderDateOnly = now.toISOString().split('T')[0];
              const orderTimeOnly = now.toTimeString().split(' ')[0];
              const orderDateTime = `${orderDateOnly} ${orderTimeOnly}`;
              
              const offlineOrderId = uuidv4();
              
              // Map orderInfo to include productName for KOT
              const mappedOrderInfo = (requestData.orderInfo || []).map(item => ({
                ...item,
                productName: item.desccription || item.productName || 'Unknown Product'
              }));
              
              const offlineOrder = {
                _id: offlineOrderId,
                orderId: offlineOrderId,
                orderInfo: mappedOrderInfo,
                transNo: orderNumber,
                name: `BIL/${orderNumber}`,
                ordName: `BIL/${orderNumber}`,
                orderDate: orderDateTime,  // Combined: "YYYY-MM-DD HH:MM:SS" - for getTimeDifference in payment
                orderTime: orderTimeOnly,  // Time in HH:MM:SS format - used by HeldOrderGridView for timer
                
                // Amounts
                subTotal: Number(requestData.subTotal) || 0,
                discountAmt: Number(requestData.discountAmt) || 0,
                amtTotal: Number(requestData.amtTotal) || 0,
                totalInclusive: Number(requestData.totalInclusive) || 0,
                totalExclusive: Number(requestData.totalExclusive) || 0,
                taxAmt: Number(requestData.taxAmt) || 0,
                serviceCharge: Number(requestData.serviceCharge) || 0,
                deliveryCharge: Number(requestData.deliveryCharge) || 0,
                packagingCharge: Number(requestData.packagingCharge || requestData.pkgCharge) || 0,
                pkgCharge: Number(requestData.pkgCharge || requestData.packagingCharge) || 0,
                roundOffAmt: Number(requestData.roundOffAmt) || 0,
                roundOff: Number(requestData.roundOff || requestData.roundOffAmt) || 0,
                
                // String fields
                ordType: requestData.ordType || 'DINE_IN',
                ordStatus: 'COMPLETED',  // Quick bill is completed immediately
                paymentStatus: 'PAID',   // Quick bill includes payment
                customerName: requestData.customerName || 'cash customer',
                customerMobile: requestData.customerMobile || '',
                floorName: requestData.floorName || '',
                aggregatorName: requestData.aggregatorName || '',
                deliveryAddress: requestData.deliveryAddress || '',
                custAddress: requestData.custAddress || {},
                notes: requestData.notes || '',
                
                // Boolean fields
                isEdited: false,
                isPaid: true,  // Quick bill is paid immediately
                isCancelled: false,
                
                // Additional fields
                paymentDetails: requestData.paymentDetails || [],
                taxDetails: requestData.taxDetails || [],
                waiterName: requestData.waiterName || '-NIL-',
                onGoing: 0,  // Quick bill is completed, not ongoing
                
                // IDs
                branchId: requestData.branchId || '',
                shiftId: requestData.shiftId || '',
                empId: requestData.empId || '',
                cusId: requestData.cusId || '',
                userId: requestData.userId || '',
                customerId: requestData.customerId || '',
                floorId: requestData.floorId || '',
                tableId: requestData.tableId || '',
                
                // Add branch/company profile data
                ...((() => {
                  const state = store.getState();
                  const companyProfile = state?.companyProfileSlice?.userValue;
                  const branches = state?.allBranchesSlice?.value || [];
                  const currentBranch = branches.find(b => b._id === requestData.branchId);
                  
                  return {
                    nameOfStore: currentBranch?.name || companyProfile?.cmpName || '',
                    email: currentBranch?.email || companyProfile?.email || '',
                    contactNumber: currentBranch?.contactNumber || companyProfile?.contactNumber || '',
                    trnNumber: currentBranch?.trnNumber || companyProfile?.trnNumber || '',
                    logo: companyProfile?.logo || '',
                    db: companyProfile?.db || localStorage.getItem('db') || ''
                  };
                })())
              };
              
              // Store offline order in IndexedDB
              try {
                await db.storeOfflineOrder(offlineOrder);
                console.log(`✅ Quick Bill order stored in IndexedDB: ${offlineOrder._id}`);
                console.log(`   Order has ${offlineOrder.orderInfo?.length || 0} items`);
                console.log(`   Order status: COMPLETED, isPaid: true, onGoing: 0`);
              } catch (error) {
                console.error('❌ Failed to store quick bill order:', error);
              }
              
              // Generate KOT token number for quick bill
              let kotToken = 1;
              try {
                kotToken = await db.getNextKotToken(
                  requestData.branchId,
                  requestData.shiftId
                );
                console.log(`🎫 Generated KOT token for Quick Bill: ${kotToken}`);
              } catch (error) {
                console.error('❌ Error generating KOT token:', error);
                kotToken = 1; // Fallback
              }
              
              // Generate KOT data for quick bill receipt
              const formattedDate = (() => {
                const d = new Date();
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const seconds = String(d.getSeconds()).padStart(2, '0');
                return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
              })();
              
              const kotData = {
                name: `${kotToken}`,
                ordName: offlineOrder.ordName,
                orderDate: formattedDate,
                ordType: offlineOrder.ordType,
                waiterName: requestData.waiterName || '-NIL-',
                orderInfo: mappedOrderInfo,
                tables: requestData.tableNames || [],
                floorName: requestData.floorName || '',
                transNo: kotToken
              };
              
              // Add kotInfo to the order for receipt display
              offlineOrder.kotInfo = [`${kotToken}`];  // Array of KOT token numbers
              
              // Update the stored order with kotInfo
              try {
                await db.cacheOrder(offlineOrder);
                console.log(`✅ Updated quick bill order with KOT token: ${kotToken}`);
              } catch (error) {
                console.error('❌ Failed to update quick bill order with KOT token:', error);
              }
              
              // Return mock response matching online structure
              mockData = {
                data: offlineOrder,
                kotData: kotData,  // Include KOT data for printing
                success: true,
                message: 'Quick Bill order created offline'
              };
            }
            
            // For addPosOrderHold, create a held/booking order offline
            if (config.url?.includes('addPosOrderHold')) {
              // Generate sequential order number
              let orderNumber = 1;
              try {
                const cachedOrders = await offlineManager.getCachedResponse('pos/listPosOrders') || [];
                const offlineOrders = await db.getOfflineOrders();
                const allOrders = [...offlineOrders, ...cachedOrders];
                
                if (allOrders.length > 0) {
                  const maxTransNo = Math.max(...allOrders.map(o => o.transNo || 0));
                  orderNumber = maxTransNo + 1;
                }
                
                console.log(`📊 Generated TEMPORARY offline hold order number: ${orderNumber}`);
              } catch (error) {
                console.error('Error calculating order number:', error);
              }
              
              const now = new Date();
              const orderDateOnly = now.toISOString().split('T')[0];
              const orderTimeOnly = now.toTimeString().split(' ')[0];
              const orderDateTime = `${orderDateOnly} ${orderTimeOnly}`;
              
              // Use the queued transaction ID as the order ID for proper ID mapping during sync
              const offlineOrderId = result.transactionId;
              
              // Map orderInfo to include productName
              const mappedOrderInfo = (requestData.orderInfo || []).map(item => ({
                ...item,
                productName: item.desccription || item.productName || 'Unknown Product'
              }));
              
              const offlineOrder = {
                _id: offlineOrderId,
                orderId: offlineOrderId,
                orderInfo: mappedOrderInfo,
                transNo: orderNumber,
                name: `BIL/${orderNumber}`,
                ordName: `BIL/${orderNumber}`,
                orderDate: orderDateTime,  // Combined: "YYYY-MM-DD HH:MM:SS" - for getTimeDifference in payment
                orderTime: orderTimeOnly,  // Time in HH:MM:SS format - used by HeldOrderGridView for timer
                
                // Amounts
                subTotal: Number(requestData.subTotal) || 0,
                discountAmt: Number(requestData.discountAmt) || 0,
                amtTotal: Number(requestData.amtTotal) || 0,
                totalInclusive: Number(requestData.totalInclusive) || 0,
                totalExclusive: Number(requestData.totalExclusive) || 0,
                taxAmt: Number(requestData.taxAmt) || 0,
                serviceCharge: Number(requestData.serviceCharge) || 0,
                deliveryCharge: Number(requestData.deliveryCharge) || 0,
                selfDeliveryKm: Number(requestData.selfDeliveryKm) || 0,
                packagingCharge: Number(requestData.packagingCharge || requestData.pkgCharge) || 0,
                pkgCharge: Number(requestData.pkgCharge || requestData.packagingCharge) || 0,
                roundOffAmt: Number(requestData.roundOffAmt) || 0,
                roundOff: Number(requestData.roundOff || requestData.roundOffAmt) || 0,
                
                // String fields - Hold order is DRAFT status
                ordType: requestData.ordType || 'DINE_IN',
                ordStatus: 'DRAFT',  // Hold order is DRAFT
                paymentStatus: 'PENDING',
                customerName: requestData.customerName || 'cash customer',
                customerMobile: requestData.customerMobile || '',
                floorName: requestData.floorName || '',
                aggregatorName: requestData.aggregatorName || '',
                deliveryAddress: requestData.deliveryAddress || '',
                custAddress: requestData.custAddress || {},
                notes: requestData.notes || '',
                
                // Boolean fields
                isEdited: false,
                isPaid: false,
                isCancelled: false,
                isHold: true,  // Mark as hold order
                
                // Additional fields
                paymentDetails: [],
                taxDetails: requestData.taxDetails || [],
                waiterName: requestData.waiterName || '-NIL-',
                onGoing: 1,  // Hold order is ongoing
                
                // IDs
                branchId: requestData.branchId || '',
                shiftId: requestData.shiftId || '',
                empId: requestData.empId || '',
                cusId: requestData.cusId || '',
                userId: requestData.userId || '',
                customerId: requestData.customerId || '',
                floorId: requestData.floorId || '',
                tableId: requestData.tableId || '',
                tableNames: requestData.tableNames || [],
                chairNames: requestData.chairNames || [],
                aggregatorId: requestData.aggregatorId || null,
                
                // Offline marker
                offlineOrder: true,
                syncStatus: 'pending',
                
                // Add branch/company profile data
                ...((() => {
                  const state = store.getState();
                  const companyProfile = state?.companyProfileSlice?.userValue;
                  const branches = state?.allBranchesSlice?.value || [];
                  const currentBranch = branches.find(b => b._id === requestData.branchId);
                  
                  return {
                    nameOfStore: currentBranch?.name || companyProfile?.cmpName || '',
                    email: currentBranch?.email || companyProfile?.email || '',
                    contactNumber: currentBranch?.contactNumber || companyProfile?.contactNumber || '',
                    trnNumber: currentBranch?.trnNumber || companyProfile?.trnNumber || '',
                    logo: companyProfile?.logo || '',
                    db: companyProfile?.db || localStorage.getItem('db') || ''
                  };
                })())
              };
              
              // Store offline hold order in IndexedDB
              try {
                await db.storeOfflineOrder(offlineOrder);
                console.log(`✅ Hold order stored in IndexedDB: ${offlineOrder._id}`);
              } catch (error) {
                console.error('❌ Failed to store hold order:', error);
              }
              
              // Return mock response matching online structure
              mockData = {
                data: offlineOrder,
                onGoing: 1,
                success: true,
                message: 'Order held offline'
              };
            }
            
            // For postPosPayment, update offline order AND queue for sync
            if (config.url?.includes('postPosPayment')) {
              const orderId = requestData.orderId || requestData.id || requestData._id;
              console.log(`💳 Processing offline payment for order: ${orderId}`);
              console.log(`   Payment request data:`, requestData);
              
              if (!orderId) {
                console.error('❌ CRITICAL: No orderId in payment request!', requestData);
                throw new Error('Order ID is required for payment');
              }
              
              try {
                // Update payment status using new atomic update method
                const updatedOrder = await db.updateOfflineOrder(orderId, {
                  isPaid: true,
                  paymentStatus: 'PAID',
                  ordStatus: 'COMPLETED',
                  paymentDetails: requestData.paymentMethod || requestData.paymentDetails || [],
                  onGoing: 0  // Completed order, not ongoing
                });
                
                if (updatedOrder) {
                  console.log(`✅ Updated offline order payment status: ${orderId}`);
                  console.log(`   isPaid: ${updatedOrder.isPaid}, paymentStatus: ${updatedOrder.paymentStatus}, onGoing: ${updatedOrder.onGoing}`);
                  console.log(`   orderInfo items: ${updatedOrder.orderInfo?.length || 0}`);
                  
                  // Ensure orderInfo is present for receipt printing
                  if (!updatedOrder.orderInfo || updatedOrder.orderInfo.length === 0) {
                    console.error('❌ CRITICAL: orderInfo is missing from updated order!');
                  }
                  
                  // IMPORTANT: Also queue the payment for sync to backend
                  console.log(`📥 Queuing payment transaction for sync (orderId: ${orderId})`);
                  const queueResult = await offlineManager.queueTransaction(config);
                  console.log(`✅ Payment queued with transactionId: ${queueResult.transactionId}, dependsOn: ${orderId}`);
                  
                  // Return EXACT same structure as online payment (status 200, data directly)
                  const mockResponse = {
                    data: updatedOrder,  // Order data DIRECTLY, not nested in mockData
                    status: 200,  // Status 200 like online payment
                    statusText: 'OK',
                    headers: { 'x-offline': 'true' },
                    config,
                    request: {}
                  };
                  
                  const error = new Error('Payment processed offline');
                  error.isOfflineQueue = true;
                  error.mockResponse = mockResponse;
                  throw error;
                } else {
                  console.warn(`⚠️ Offline order not found for payment: ${orderId}`);
                  // Queue since generic queue was skipped for this endpoint
                  await offlineManager.queueTransaction(config);
                }
              } catch (error) {
                // If it's our custom error, re-throw it
                if (error.isOfflineQueue) {
                  throw error;
                }
                console.error('❌ Error processing offline payment:', error);
                // Queue since generic queue was skipped for this endpoint
                await offlineManager.queueTransaction(config);
              }
            }
            
            // For addItemsToExistingOrders, update offline order with new items
            if (config.url?.includes('addItemsToExistingOrders')) {
              const orderId = requestData.id || requestData.orderId || requestData._id;
              console.log(`📝 Processing offline order update for: ${orderId}`);
              console.log(`   Update request data:`, requestData);
              
              try {
                // Get the existing order (could be offline order OR cached online order)
                let existingOrder = await db.getOfflineOrderById(orderId);
                
                // If not found in offline orders, check all cached orders
                if (!existingOrder) {
                  console.log(`   Order not in offline orders, checking all cached orders...`);
                  existingOrder = await db.ordersCache.get(orderId);
                  console.log(`   Result from ordersCache.get:`, existingOrder ? 'Found' : 'Not found');
                  
                  // If still not found, list all orders to debug
                  if (!existingOrder) {
                    const allOrders = await db.ordersCache.toArray();
                    console.log(`   Total orders in cache: ${allOrders.length}`);
                    console.log(`   Looking for order ID: ${orderId}`);
                    console.log(`   Sample order IDs in cache:`, allOrders.slice(0, 3).map(o => o._id));
                  }
                }
                
                if (existingOrder) {
                  console.log(`   Found order to update: ${orderId} (offlineOrder: ${existingOrder.offlineOrder})`);

                  // Merge new items with existing items
                  const existingItems = existingOrder.orderInfo || [];
                  const newItems = requestData.orderInfo || [];
                  
                  // Update the order with new items and other fields
                  // Use ?? (nullish coalescing) instead of || to preserve 0 values
                  const updatedOrder = {
                    ...existingOrder,
                    orderInfo: [...existingItems, ...newItems],
                    subTotal: requestData.subTotal ?? existingOrder.subTotal,
                    totalAmt: requestData.totalAmt ?? existingOrder.totalAmt,
                    amtTotal: requestData.amtTotal ?? existingOrder.amtTotal,
                    discountAmt: requestData.discountAmt ?? existingOrder.discountAmt,
                    taxAmt: requestData.taxAmt ?? existingOrder.taxAmt,
                    deliveryCharge: requestData.deliveryCharge ?? existingOrder.deliveryCharge,
                    pkgCharge: requestData.pkgCharge ?? existingOrder.pkgCharge,
                    roundOff: requestData.roundOff ?? existingOrder.roundOff,
                    updatedAt: new Date().toISOString()
                  };
                  
                  // Store updated order
                  await db.storeOfflineOrder(updatedOrder);
                  console.log(`✅ Updated offline order: ${orderId} (added ${newItems.length} items)`);
                  
                  // Generate KOT data for the new items
                  const kotData = {
                    ordName: updatedOrder.name || updatedOrder.ordName,
                    transNo: updatedOrder.transNo,
                    orderDate: updatedOrder.orderDate,
                    orderInfo: newItems.map(item => ({
                      ...item,
                      productName: item.productName || item.desccription
                    })),
                    waiterName: updatedOrder.waiterName || '-NIL-',
                    ordType: updatedOrder.ordType,
                    tables: updatedOrder.tableNames || [],
                    floorName: updatedOrder.floorName || ''
                  };
                  
                  // Return success response matching online structure
                  const mockResponse = {
                    data: {
                      data: updatedOrder,
                      kotData: kotData,
                      onGoing: updatedOrder.onGoing || 1,
                      success: true,
                      message: 'Order updated offline'
                    },
                    status: 200,
                    statusText: 'OK',
                    headers: { 'x-offline': 'true' },
                    config,
                    request: {}
                  };
                  
                  const error = new Error('Order updated offline');
                  error.isOfflineQueue = true;
                  error.mockResponse = mockResponse;
                  throw error;
                } else {
                  console.warn(`⚠️ Order not found for update: ${orderId}`);
                  console.warn(`   This order is not in IndexedDB cache. It may have been:`)
                  console.warn(`   1. Created online and not yet cached`);
                  console.warn(`   2. Cleared from cache`);
                  console.warn(`   3. Using a different ID format`);
                  console.warn(`   The update will be queued for when you're back online.`);
                  // Queue since generic queue was skipped for this endpoint
                  await offlineManager.queueTransaction(config);
                }
              } catch (error) {
                // If it's our custom error, re-throw it
                if (error.isOfflineQueue) {
                  throw error;
                }
                console.error('❌ Error updating offline order:', error);
                // Queue since generic queue was skipped for this endpoint
                await offlineManager.queueTransaction(config);
              }
            }
            
            // For empCheckOut, queue the checkout and return mock response
            if (config.url?.includes('empCheckOut') && !config.url?.includes('empCheckOutPaymentReport')) {
              console.log(`📴 Offline: Employee checkout requested`);
              
              // Queue the checkout for sync
              const queueResult = await offlineManager.queueTransaction(config);
              console.log(`✅ Employee checkout queued with transactionId: ${queueResult.transactionId}`);
              
              // Store local checkout status
              const checkInId = requestData.checkInId;
              if (checkInId) {
                try {
                  await db.settings.put({
                    key: `pendingCheckout_${checkInId}`,
                    value: {
                      checkInId,
                      queuedAt: Date.now(),
                      transactionId: queueResult.transactionId
                    }
                  });
                } catch (e) {
                  console.warn('Could not store pending checkout status:', e);
                }
              }
              
              // Return mock success response
              const mockResponse = {
                data: {
                  success: true,
                  offline: true,
                  message: 'Checkout queued - will sync when online',
                  data: {
                    checkInId: requestData.checkInId,
                    checkOutTime: new Date().toISOString()
                  }
                },
                status: 200,
                statusText: 'OK',
                headers: { 'x-offline': 'true' },
                config,
                request: {}
              };
              
              const error = new Error('Checkout queued offline');
              error.isOfflineQueue = true;
              error.mockResponse = mockResponse;
              throw error;
            }
            
            // For cancelOngoingOrders OR deleteBookingOrder, update offline order status AND queue for sync
            if (config.url?.includes('cancelOngoingOrders') || config.url?.includes('deleteBookingOrder')) {
              const orderId = requestData.id || requestData.orderId || requestData._id;
              console.log(`🚫 Processing offline order cancel for: ${orderId}`);
              
              try {
                // Get the offline order
                const offlineOrder = await db.getOfflineOrderById(orderId);
                
                if (offlineOrder) {
                  // Update cancel status
                  const cancelledOrder = {
                    ...offlineOrder,
                    isCancelled: true,
                    ordStatus: 'CANCELLED',
                    updatedAt: new Date().toISOString()
                  };
                  
                  // Update in IndexedDB
                  await db.storeOfflineOrder(cancelledOrder);
                  console.log(`✅ Cancelled offline order: ${orderId}`);
                  
                  // IMPORTANT: Also queue the cancellation for sync to backend
                  console.log(`📥 Queuing cancellation transaction for sync (orderId: ${orderId})`);
                  const queueResult = await offlineManager.queueTransaction(config);
                  console.log(`✅ Cancellation queued with transactionId: ${queueResult.transactionId}, dependsOn: ${orderId}`);
                  
                  // Return success response (status 200)
                  const mockResponse = {
                    data: { success: true, message: 'Order cancelled offline' },
                    status: 200,
                    statusText: 'OK',
                    headers: { 'x-offline': 'true' },
                    config,
                    request: {}
                  };
                  
                  const error = new Error('Order cancelled offline');
                  error.isOfflineQueue = true;
                  error.mockResponse = mockResponse;
                  throw error;
                } else {
                  console.warn(`⚠️ Offline order not found for cancel: ${orderId}`);
                  // Queue since generic queue was skipped for this endpoint
                  await offlineManager.queueTransaction(config);
                }
              } catch (error) {
                // If it's our custom error, re-throw it
                if (error.isOfflineQueue) {
                  throw error;
                }
                console.error('❌ Error cancelling offline order:', error);
                // Queue since generic queue was skipped for this endpoint
                await offlineManager.queueTransaction(config);
              }
            }
            
            const mockResponse = {
              data: mockData,
              status: 202,
              statusText: 'Queued',
              headers: { 'x-offline': 'true' },
              config,
              request: {}
            };

            const error = new Error('Request queued for offline sync');
            error.isOfflineQueue = true;
            error.mockResponse = mockResponse;
            throw error;
          }
          
          // Special handling for viewPosOrderById - check offline orders FIRST before cache
          if (config.url?.includes('viewPosOrderById') && method === 'post') {
            try {
              const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
              const orderId = requestData?.id;
              
              console.log(`🔍 viewPosOrderById called for: ${orderId}`);
              
              if (orderId) {
                const offlineOrder = await db.getOfflineOrderById(orderId);
                
                if (offlineOrder) {
                  console.log(`✅ Serving offline order from IndexedDB: ${orderId}`);
                  console.log(`   Order has ${offlineOrder.orderInfo?.length || 0} items`);
                  console.log(`   Order status: ${offlineOrder.ordStatus}, isPaid: ${offlineOrder.isPaid}`);
                  
                  const mockResponse = {
                    data: offlineOrder,
                    status: 200,
                    statusText: 'OK (from IndexedDB)',
                    headers: { 'x-offline-order': 'true' },
                    config,
                    request: {}
                  };
                  
                  const error = new Error('Serving offline order from IndexedDB');
                  error.isFromCache = true;
                  error.mockResponse = mockResponse;
                  throw error;
                } else {
                  console.log(`ℹ️ Order ${orderId} not found in offline orders, checking cache...`);
                }
              }
            } catch (error) {
              if (error.isFromCache) {
                throw error;
              }
              // Continue to cache check if not an offline order
              console.error('Error checking offline order:', error);
            }
          }
          
          // Skip cache for authentication/security endpoints - must always fail when offline
          const authEndpoints = /(checkdevice|requestdeviceotp|verifydeviceotp|login|trusteddevices|removedevice|searchuser)/i;
          if (authEndpoints.test(config.url || '')) {
            console.log(`🔒 Auth endpoint ${config.url} - not serving from cache (security)`);
            throw new Error('Authentication endpoints require network connection');
          }
          
          // Try to get from cache (for read operations only)
          const cachedData = await offlineManager.getCachedResponse(cacheKey);
          
          if (cachedData) {
            console.log(`✅ Served ${config.url} from cache`);
            
            let responseData = cachedData || [];
            
            // If listing orders, merge with offline orders
            if (config.url?.includes('listPosOrders')) {
              try {
                const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
                const requestType = requestData?.type;
                const requestBranchId = requestData?.branchId;
                const requestWaiterId = requestData?.waiterId;
                const offlineOrders = await db.getOfflineOrders();

                const getId = (value) => {
                  if (!value) return null;
                  return (value?._id || value).toString();
                };
                const branchIdStr = getId(requestBranchId);
                const waiterIdStr = getId(requestWaiterId);

                const filteredByBranchAndWaiter = offlineOrders.filter((order) => {
                  const orderBranchId = getId(order.branchId);
                  const orderWaiterId = getId(order.waiterId);

                  if (branchIdStr && orderBranchId && branchIdStr !== orderBranchId) return false;
                  if (waiterIdStr && orderWaiterId && waiterIdStr !== orderWaiterId) return false;
                  return true;
                });

                let filteredOfflineOrders;

                if (requestType === 2) {
                  filteredOfflineOrders = filteredByBranchAndWaiter.filter((order) => !order.isCancelled);
                  console.log(`📋 View Orders: Including ALL ${filteredOfflineOrders.length} offline orders (same as online)`);
                } else {
                  filteredOfflineOrders = filteredByBranchAndWaiter.filter((order) => {
                    const status = (order.status || order.ordStatus || '').toString();
                    const isOngoingOnline = ['DRAFT', 'BOOKING', 'CHDRAFT'].includes(status);
                    const isOngoingOffline = status === 'PENDING';
                    return (isOngoingOnline || isOngoingOffline) && !order.isCancelled;
                  });
                  console.log(`📦 Ongoing Orders: ${filteredOfflineOrders.length} orders (after branch/waiter filter)`);
                }

                if (filteredOfflineOrders.length > 0) {
                  responseData = [...filteredOfflineOrders, ...cachedData];
                } else {
                  responseData = cachedData;
                }
              } catch (error) {
                console.error('Error merging offline orders:', error);
              }
            }
            
            // Return cached response
            const mockResponse = {
              data: responseData,
              status: 200,
              statusText: 'OK (from cache)',
              headers: { 'x-from-cache': 'true' },
              config,
              request: {}
            };
            
            // Throw special error to return cached data
            const error = new Error('Serving from cache');
            error.isFromCache = true;
            error.mockResponse = mockResponse;
            throw error;
          } else {
            // No cache found - but for listPosOrders, still return offline orders
            if (config.url?.includes('listPosOrders')) {
              try {
                const requestData = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
                const requestType = requestData?.type;
                const requestBranchId = requestData?.branchId;
                const requestWaiterId = requestData?.waiterId;
                const offlineOrders = await db.getOfflineOrders();

                const getId = (value) => {
                  if (!value) return null;
                  return (value?._id || value).toString();
                };
                const branchIdStr = getId(requestBranchId);
                const waiterIdStr = getId(requestWaiterId);

                const filteredByBranchAndWaiter = offlineOrders.filter((order) => {
                  const orderBranchId = getId(order.branchId);
                  const orderWaiterId = getId(order.waiterId);

                  if (branchIdStr && orderBranchId && branchIdStr !== orderBranchId) return false;
                  if (waiterIdStr && orderWaiterId && waiterIdStr !== orderWaiterId) return false;
                  return true;
                });

                let filteredOfflineOrders;

                if (requestType === 2) {
                  filteredOfflineOrders = filteredByBranchAndWaiter.filter((order) => !order.isCancelled);
                  console.log(`📋 View Orders (no cache): Returning ${filteredOfflineOrders.length} offline orders`);
                } else {
                  filteredOfflineOrders = filteredByBranchAndWaiter.filter((order) => {
                    const status = (order.status || order.ordStatus || '').toString();
                    const isOngoingOnline = ['DRAFT', 'BOOKING', 'CHDRAFT'].includes(status);
                    const isOngoingOffline = status === 'PENDING';
                    return (isOngoingOnline || isOngoingOffline) && !order.isCancelled;
                  });
                  console.log(`📦 Ongoing Orders (no cache): Returning ${filteredOfflineOrders.length} orders (after branch/waiter filter)`);
                }

                // Return offline orders even without cache
                const mockResponse = {
                  data: filteredOfflineOrders,
                  status: 200,
                  statusText: 'OK (offline orders)',
                  headers: { 'x-from-offline': 'true' },
                  config,
                  request: {}
                };

                const error = new Error('Serving offline orders');
                error.isFromCache = true;
                error.mockResponse = mockResponse;
                throw error;
              } catch (offlineError) {
                if (offlineError.isFromCache) throw offlineError;
                console.error('Error getting offline orders:', offlineError);
              }
            }
            
            // No cache found - queue the request if it's POST
            if (method === 'post') {
              console.log(`📴 Offline: No cache, queuing ${config.method} ${config.url}`);
              
              const result = await offlineManager.queueTransaction(config);
              
              const mockResponse = {
                data: {
                  success: true,
                  queued: true,
                  transactionId: result.transactionId,
                  message: 'Transaction queued for sync when online'
                },
                status: 202,
                statusText: 'Queued',
                headers: {},
                config,
                request: {}
              };

              const error = new Error('Request queued for offline sync');
              error.isOfflineQueue = true;
              error.mockResponse = mockResponse;
              throw error;
            }
          }
        } catch (error) {
          if (error.isFromCache || error.isOfflineQueue) {
            throw error;
          }
          console.error('Error handling offline request:', error);
          // If cache/queue fails, let request proceed and fail naturally
          console.log(`⚠️ Error handling offline, request will fail`);
        }
      }

      // Add timestamp to request
      config.metadata = {
        requestTime: new Date().toISOString()
      };

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  /**
   * Response Interceptor
   * Handle offline queue responses and errors
   */
  axios.interceptors.response.use(
    async (response) => {
      const skipCache = String(response?.config?.headers?.['x-skip-cache'] || response?.config?.headers?.['X-Skip-Cache'] || '').toLowerCase() === 'true';

      // Log successful requests
      if (response.config.metadata) {
        const duration = Date.now() - new Date(response.config.metadata.requestTime).getTime();
        console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
      }

      // Skip offline logic for non-backend responses (e.g. localhost:5000 print utility)
      const respUrl = response.config?.url || '';
      if (respUrl.startsWith('http://') || respUrl.startsWith('https://')) {
        return response;
      }

      // Backend recovery detection: If we get a successful response and have pending transactions, auto-sync
      // This handles the case where backend crashed and restarted (network never went offline)
      if (!window._backendHealthy) {
        window._backendHealthy = true;
        const pendingCount = await db.getPendingCount();
        if (pendingCount > 0 && !offlineManager.syncInProgress) {
          console.log(`🔄 Backend recovered! Found ${pendingCount} pending transactions, auto-syncing...`);
          // Delay sync slightly to let the current request complete
          // Use silent mode to avoid conflicting with other sync triggers
          setTimeout(() => {
            offlineManager.syncPendingTransactions(true).catch(err => 
              console.error('Auto-sync after backend recovery failed:', err)
            );
          }, 2000);
        }
      }

      // Cache successful GET and POST responses (POST is used for reads in this app)
      const method = response.config.method?.toLowerCase();
      if (!skipCache && (method === 'get' || method === 'post') && response.data) {
        try {
          // Extract URL path (use URL as cache key)
          const urlPath = response.config.url?.replace(/^https?:\/\/[^\/]+/, '') || response.config.url;

          // Heavy reports (shift reports, summaries, PDF data) can be large – skip caching to avoid UI stalls
          // Also skip caching for authentication/device verification endpoints (must always be fresh)
          const skipCachePattern = /(shiftreport|advanceShift|salesSummeryReport|SalesSummeryReport|SalesSummary|reportPrint|export|checkdevice|requestdeviceotp|verifydeviceotp|login|trusteddevices|searchuser|viewExpenseReport)/i;
          const shouldSkipCache = skipCachePattern.test(urlPath || '');

          if (shouldSkipCache) {
            console.log(`🧯 Skipping cache for heavy endpoint: ${urlPath}`);
          } else {
            // Fire-and-forget cache write to keep UI responsive
            offlineManager.cacheResponse(urlPath, response.data)
              .then(() => console.log(`💾 Cached: ${urlPath}`))
              .catch(cacheError => console.error('Error caching response:', cacheError));
          }

          // Also cache individual orders to ordersCache for offline viewing
          if (response.config.url?.includes('listPosOrders') && response.data?.data) {
            const orders = Array.isArray(response.data.data) ? response.data.data : [];
            const kotDataList = Array.isArray(response.data.kotData) ? response.data.kotData : [];
            let cachedCount = 0;
            for (const order of orders) {
              // Only cache if has minimum required fields
              if (order._id && order.orderInfo) {
                // Find matching KOT data for this order
                const matchingKot = kotDataList.find(kot => kot.orderId === order._id);
                await db.cacheOrder({
                  ...order,
                  kotTransNo: matchingKot?.transNo || null,  // Store KOT token number
                  offlineOrder: false,
                  syncStatus: 'synced'
                });
                cachedCount++;
              }
            }
            console.log(`💾 Cached ${cachedCount}/${orders.length} orders to ordersCache for offline viewing`);
          }

          // Also cache individual order when viewPosOrderById is called
          if (response.config.url?.includes('viewPosOrderById') && response.data?.data) {
            const order = response.data.data;
            // Only cache if has minimum required fields
            if (order._id && order.orderInfo) {
              await db.cacheOrder({
                ...order,
                offlineOrder: false,
                syncStatus: 'synced'
              });
              console.log(`💾 Cached individual order ${order._id} for offline use`);
            }
          }

          // Cache KOT token when order is created online
          if ((response.config.url?.includes('addPosOrder') || response.config.url?.includes('addQuickBill')) && response.data?.kotData) {
            const order = response.data.data;
            const kotData = response.data.kotData;
            if (order?._id && kotData?.transNo) {
              // Cache the order with KOT token
              await db.cacheOrder({
                ...order,
                kotTransNo: kotData.transNo,  // Store KOT token number
                kotInfo: [kotData.name || `${kotData.transNo}`],  // Store for receipt display
                offlineOrder: false,
                syncStatus: 'synced'
              });
              console.log(`💾 Cached new order ${order._id} with KOT token ${kotData.transNo}`);
              
              // Update the last online KOT token for offline continuation
              await db.updateLastOnlineKotToken(
                order.branchId,
                order.shiftId,
                kotData.transNo
              );
              
              // Reset the offline counter keys so it starts fresh from this online token
              // Delete both the specific shift key and the 'current' fallback
              const counterKeySpecific = `kotCounter_${order.branchId}_${order.shiftId || 'current'}`;
              const counterKeyCurrent = `kotCounter_${order.branchId}_current`;
              try {
                await db.settings.delete(counterKeySpecific);
                await db.settings.delete(counterKeyCurrent);
                console.log(`🔄 Reset offline KOT counters (${counterKeySpecific}, ${counterKeyCurrent}) to sync with online token ${kotData.transNo}`);
              } catch (e) {
                console.warn('⚠️ Failed resetting KOT counter keys', e);
              }
            }
          }
        } catch (error) {
          console.error('Error caching response:', error);
        }
      }

      return response;
    },
    async (error) => {
      // Handle offline queue responses
      if (error.isOfflineQueue) {
        return Promise.resolve(error.mockResponse);
      }
      
      // Handle cached responses
      if (error.isFromCache) {
        return Promise.resolve(error.mockResponse);
      }

      // Skip offline logic for non-backend requests (e.g. localhost:5000 print utility)
      const errUrl = error.config?.url || '';
      if (errUrl.startsWith('http://') || errUrl.startsWith('https://')) {
        return Promise.reject(error);
      }

      const originalConfig = error.config;
      const status = error.response?.status;

      // Let validation/authorization errors bubble up so the UI can show the backend message
      if (status === 406 || status === 401 || status === 403 || status === 400) {
        return Promise.reject(error);
      }

      // Special handling for endpoints that return 404 when no data exists (not an error)
      const isShiftEndpoint = originalConfig?.url?.includes('startmanualshift') ||
                              originalConfig?.url?.includes('endmanualshift') ||
                              originalConfig?.url?.includes('viewactiveshift');
      
      const isSpecialOfferEndpoint = originalConfig?.url?.includes('viewSpecialOffer');
      
      if (status === 404 && (isShiftEndpoint || isSpecialOfferEndpoint)) {
        console.log('⚠️ 404 on endpoint - no data available, not retrying with cache:', originalConfig?.url);
        return Promise.reject(error);
      }

      // Don't retry 4xx client errors (404, 400, etc.) - they will never succeed
      const isClientError = error.response && error.response.status >= 400 && error.response.status < 500;
      
      // Auth/security endpoints and read-only report POSTs should NEVER be retried with offline fallback or queued
      const authEndpointPattern = /(checkdevice|requestdeviceotp|verifydeviceotp|login|trusteddevices|removedevice|searchuser|verifypassword|getlog|viewlog|viewExpenseReport|accountReceivableAgingReport|accountPayableAgingReport|singleCustomerARAR|accountReceivableReport|accountPayableReport|agingReport|Report$)/i;
      const isAuthEndpoint = authEndpointPattern.test(originalConfig?.url || '');

      // Retry once with forced offline fallback so the request interceptor can handle it
      // BUT: Don't retry sync requests - they should just fail and be retried later
      const requestUrl = originalConfig?.url || '';
      const isExternalRequest = requestUrl.startsWith('http://') || requestUrl.startsWith('https://');
      const canRetryWithOfflineFallback =
        originalConfig &&
        !originalConfig._offlineRetried &&
        !originalConfig._isSyncRetry && // Don't retry sync engine requests
        !isClientError && // Don't retry client errors
        !isAuthEndpoint && // Don't retry auth endpoints - they must always be fresh
        !isExternalRequest && // Don't retry non-backend requests (print utility, etc.)
        (originalConfig.method?.toLowerCase() === 'post' || originalConfig.method?.toLowerCase() === 'get');

      if (canRetryWithOfflineFallback) {
        console.warn('⚠️ Request failed, retrying with forced offline handler:', originalConfig.url);

        const retryConfig = {
          ...originalConfig,
          _forceOffline: true,
          _offlineRetried: true
        };

        // Axios removes data when spreading config that already has data as stringified body
        if (originalConfig.data) {
          retryConfig.data = originalConfig.data;
        }

        try {
          return await axios.request(retryConfig);
        } catch (retryError) {
          console.error('❌ Forced offline retry also failed:', retryError);
          return Promise.reject(retryError);
        }
      }
      
      // Log client errors once for debugging
      if (isClientError) {
        console.warn(`⚠️ Client error ${error.response.status} - not queuing: ${originalConfig?.url}`);
      }

      // Handle network errors (including server down while still "online")
      if (!error.response && originalConfig) {
        console.error('Network error:', error.message);
        
        // Mark backend as unhealthy so we can detect recovery later
        window._backendHealthy = false;
        
        // For write requests, queue them and simulate offline response
        // But never queue auth endpoints - they must fail properly
        if (originalConfig.method?.toLowerCase() !== 'get' && !isAuthEndpoint) {
          try {
            const result = await offlineManager.queueTransaction({
              ...originalConfig,
              _forceOffline: true
            });
            return Promise.resolve({
              data: {
                success: true,
                queued: true,
                transactionId: result.transactionId,
                message: 'Transaction queued due to unreachable server'
              },
              status: 202,
              statusText: 'Queued',
              headers: { 'x-offline-queued': 'true' },
              config: originalConfig,
              request: {}
            });
          } catch (queueError) {
            console.error('Error queuing failed request:', queueError);
          }
        }
      }

      // Handle HTTP errors
      if (error.response && originalConfig) {
        const status = error.response.status;
        
        // 5xx server errors should trigger offline queue for writes
        // But never queue auth endpoints - they must fail properly
        if (status >= 500 && originalConfig.method?.toLowerCase() !== 'get' && !isAuthEndpoint) {
          try {
            const result = await offlineManager.queueTransaction({
              ...originalConfig,
              _forceOffline: true
            });
            return Promise.resolve({
              data: {
                success: true,
                queued: true,
                transactionId: result.transactionId,
                message: 'Transaction queued - server error'
              },
              status: 202,
              statusText: 'Queued',
              headers: { 'x-offline-queued': 'true' },
              config: originalConfig,
              request: {}
            });
          } catch (queueError) {
            console.error('Error queueing server error request:', queueError);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  // Initialize backend health flag
  window._backendHealthy = true;
  
  // Purge stale read-only report transactions that should never have been queued
  const staleReportEndpoints = [
    'accountReceivableAgingReport', 'accountPayableAgingReport', 'singleCustomerARAR',
    'accountReceivableReport', 'accountPayableReport', 'agingReport', 'inOutVatReport',
    'viewExpenseReport', 'viewlog', 'getlog', 'listTimeReport', 'shiftReportSale',
    'shiftReportProducts', 'shiftReportPaymentSummary', 'shiftSummeryReport',
    'salesDetailsReport', 'aggregatorReport',
  ];
  setTimeout(async () => {
    try {
      const allPending = await db.getPendingTransactions();
      let purged = 0;
      for (const txn of allPending) {
        if (staleReportEndpoints.some(e => (txn.endpoint || '').includes(e))) {
          await db.deleteTransaction(txn.transactionId);
          purged++;
        }
      }
      if (purged > 0) console.log(`🗑️ Purged ${purged} stale report transactions from sync queue`);
    } catch (err) {
      console.error('Error purging stale report transactions:', err);
    }
  }, 500);

  // Check for pending transactions from previous session (e.g., backend was down, now restarted)
  setTimeout(async () => {
    try {
      if (offlineManager.syncInProgress) return; // Another sync source already running
      const pendingCount = await db.getPendingCount();
      if (pendingCount > 0) {
        // First check if server is actually reachable before trying to sync
        const serverReachable = await offlineManager.checkServerReachable();
        if (!serverReachable) {
          console.log(`📴 Found ${pendingCount} pending transactions but server is not reachable. Will sync when online.`);
          return;
        }
        console.log(`🔄 Found ${pendingCount} pending transactions from previous session, auto-syncing...`);
        await offlineManager.syncPendingTransactions(true); // Silent mode - don't trigger UI refresh
      }
    } catch (err) {
      console.error('Error checking pending transactions on startup:', err);
    }
  }, 3000); // Wait 3 seconds for app to stabilize
  
  console.log('✅ Offline interceptor configured');
};

/**
 * Check if response is from offline queue
 */
export const isQueuedResponse = (response) => {
  return response?.data?.queued === true && response?.data?.transactionId;
};

/**
 * Get transaction ID from queued response
 */
export const getTransactionId = (response) => {
  if (isQueuedResponse(response)) {
    return response.data.transactionId;
  }
  return null;
};

export default setupOfflineInterceptor;
